import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import TR from './../../Trans';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import ErrorDialog from '../dialogs/ErrorDialog';
import { Capacitor } from '@capacitor/core';
import { getOS } from './../../services/os';
import ThemeToggleButton from '../items/ThemeToggleButton';
import Divider from '@mui/material/Divider';
import IosExternalLinkDialog from '../dialogs/IosExternalLinkDialog';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useLocalStorage } from '../../services/useLocalStorage';
import muvikaAttrs from './../../attrs';
import { ReactComponent as MuvikaLogo } from './../../assets/logo-horiz.svg';
import { ReactComponent as MuvikaLogoInv } from './../../assets/logo-horiz-inv.svg';
import { ReactComponent as MuvikaLogoProInv } from './../../assets/logo-horiz-pro-inv.svg';
import Avatar from '@mui/material/Avatar';
import CssBaseline from '@mui/material/CssBaseline';
import TermsDialog from '../dialogs/TermsDialog';
import FormControlLabel from '@mui/material/FormControlLabel';
import { GlobalStyles } from "@mui/material";
import backgroundImage from "../../assets/people-group-4-square.jpg"; 
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormGroup from '@mui/material/FormGroup';
import Select from '@mui/material/Select';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import OutlinedInput from '@mui/material/OutlinedInput';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import Error from './../items/Error';
import ChooseLanguage from './../items/ChooseLanguage';
import Message from './../items/Message';
import NewAccountDialog from '../dialogs/NewAccountDialog';
import Container from '@mui/material/Container';
import Waiting from '../items/Waiting';
import MuvikaService from './../../services/muvika';
import sessionStore from './../../stores/session';
import messageStore from '../../stores/message';
import accountStore from '../../stores/account';
import environmentStore from './../../stores/environment';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: "var(--back-color)"
    }
  },
});

function NewAccount() {

  useEffect(() => {
    document.documentElement.style.fontSize = "16px";
    window.dispatchEvent(new Event('refitbuttons'));
    window.dispatchEvent(new Event('resize'));
  }, []);

  const isSessionOpen = sessionStore((state) => state.isSessionOpen);
  const sessionToken = sessionStore((state) => state.sessionToken);
  const isLoggedIn = sessionStore((state) => state.isLoggedIn);
  const oneTimeLogin = sessionStore((state) => state.oneTimeLogin);
  const account = accountStore((state) => state.account);
  const setError = messageStore((state) => state.setError);
  const environment = environmentStore((state) => state.environment);
  const getAccount = accountStore((state) => state.getAccount);
  const urls = accountStore((state) => state.urls);

  const [isIosExternalLinkDialogOpen, setIsIosExternalLinkDialogOpen] = useState(false);

  const handleOpenIosExternalLinkDialog = () => {
    setIsIosExternalLinkDialogOpen(true);
  };

  const handleCloseIosExternalLinkDialog = () => {
    setIsIosExternalLinkDialogOpen(false);
  };

  const [isNewAccountDialogOpen, setIsNewAccountDialogOpen] = useState(false);

  let navigate = useNavigate();

  const handleOpenNewAccountDialog = () => {
    setIsNewAccountDialogOpen(true);
  };

  const handleCloseNewAccountDialog = () => {
    setIsNewAccountDialogOpen(false);
  };

  useEffect(() => {
    if (!isLoggedIn && account) {
      if (!(Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android')) {
        oneTimeLogin(account.username, account.code);
      }
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      getAccount(sessionToken);
    }
  }, [isLoggedIn]);

  const handleMoveToApp = () => {
    oneTimeLogin(account.username, account.code);
  };

  const handleOk = () => {
    if (account) {
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
        setIsNewAccountDialogOpen(true);
      } else {
        MuvikaService.createAccountLink(sessionToken, account.username, "one_time_login")
          .then(function (res) {
            //console.log(res);
            if (res.data.error) {
              setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
            } else {
              if (Capacitor.isNativePlatform()) {
                setTimeout(() => navigate("/waitingaccount"), 100);
                window.open(urls.url_subscribe + "?origin=muvikaapp&id=" + account.account_id + "&login_code=" + res.data.data.code, "_self");
              } else {
                window.open(urls.url_subscribe + "?origin=muvikaweb&id=" + account.account_id + "&login_code=" + res.data.data.code, "_self");
              }
            }
          })
          .catch(function () { });
      }
    }
  };

  const navigateToRootIfAndroidLoggedIn = () => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android' && isSessionOpen && isLoggedIn) {
      navigate("/");
    }
  };

  const [theme, setTheme] = useLocalStorage(muvikaAttrs.service + "-theme", "dark");
  
    const renderLogo = () => {
      if (!theme || theme === "dark") {
        return <MuvikaLogo className="muvika-logo-svg" />;
      } else {
        return <MuvikaLogoInv className="muvika-logo-svg" />;
      }
    };

  const renderContainer = () => {
    if (isSessionOpen) {
      return <Container className="muvika-entry-container" component="main" maxWidth="xs">
        <CssBaseline />
        <Error />
        <Message />
        <IosExternalLinkDialog
          open={isIosExternalLinkDialogOpen}
          onClose={handleCloseIosExternalLinkDialog}
          message={TR.cm_external_link_account_ios}
          uurl={"https://muvika.com"}
        />
        <NewAccountDialog
          open={isNewAccountDialogOpen}
          onClose={handleCloseNewAccountDialog}
          onOk={handleMoveToApp}
        />
        <Box
          className="muvika-entry-box"
        >
          <p>
            <a href="#/">{renderLogo()}</a>
          </p>
          <Box sx={{ margin: 2 }}>
            <Typography component="p" variant="p">
              {TR.ww_account_created}. {TR.cm_redeem_your_free_period}.
            </Typography>
          </Box>
          <Stack direction="row" spacing={6} justifyContent="center">
            <Button variant="text" color="success" onClick={
              handleOk
            }>Ok</Button>
          </Stack>
          <ThemeToggleButton />
        </Box>
      </Container>;
    } else {
      return <Navigate to="/login" />;
    }
  };

  const overflowStyleIos = `
  html {
    overflow-y:scroll !important;
  }
  
  body {
    overflow-y:scroll !important;
  }
  
  main {
    overflow-y:scroll !important;
  }
`;

  const overflowStyle = `
  html {
    overflow-y:auto !important;
  }
  
  body {
    overflow-y:auto !important;
  }
  
  main {
    overflow-y:auto !important;
  }
`;

  const renderOverflowStyle = () => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      return <style>
        {overflowStyleIos}
      </style>;
    } else {
      return <style>
        {overflowStyle}
      </style>;
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <GlobalStyles
        styles={{
          body: {
            margin: 0,
            minHeight: "100vh",
            minWidth:"100vw",
            backgroundImage: `url(${backgroundImage}) !important`,
            backgroundRepeat: "no-repeat !important",
            backgroundSize: "cover !important",
            backgroundPosition: "center !important",
          },
        }}
      />
      {renderOverflowStyle()}
      {navigateToRootIfAndroidLoggedIn()}
      {renderContainer()}
    </ThemeProvider>
  );
}

export default NewAccount;